import React, { useState } from 'react';
import { KeyRound } from 'lucide-react';
import { createPasswordResetLink, isApiError, type PasswordResetLink } from '../services/api';
import { CopyLinkButton } from './AdminInvites';

interface AdminResetLinkProps {
  userId: string;
  username: string;
}

/** Heure locale (hh:mm) d'une date ISO, pour dire jusqu'à quand le lien est valable. */
const formatTime = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
};

/**
 * Lien de réinitialisation de mot de passe pour un élève, dans le détail d'un élève du tableau de bord.
 * Aucun e-mail n'est envoyé : l'administrateur copie le lien et le transmet lui-même (les e-mails de la
 * plateforme partent en indésirables). Générer un nouveau lien annule les précédents.
 */
const AdminResetLink: React.FC<AdminResetLinkProps> = ({ userId, username }) => {
  const [link, setLink] = useState<PasswordResetLink | null>(null);
  const [error, setError] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  const handleGenerate = async () => {
    setError('');
    setIsGenerating(true);
    try {
      setLink(await createPasswordResetLink(userId));
    } catch (err) {
      setLink(null);
      setError(isApiError(err) && !err.isNetworkError
        ? err.message || 'Impossible de générer le lien.'
        : 'Serveur injoignable.');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="mt-6 border-t border-gray-200 dark:border-gray-700 pt-4" data-testid="admin-reset-link">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Mot de passe oublié</h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
        Génère un lien à transmettre à {username} (Discord, message…). Aucun e-mail n'est envoyé. Le lien
        sert une seule fois, dure {link?.expiresInMinutes ?? 60} minutes et annule les liens précédents de cet élève.
      </p>
      <button
        type="button"
        onClick={handleGenerate}
        disabled={isGenerating}
        className="inline-flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
      >
        <KeyRound className="w-4 h-4" />
        {isGenerating ? 'Génération...' : 'Générer un lien de réinitialisation'}
      </button>

      {error && (
        <p role="alert" className="mt-3 text-sm text-red-700 dark:text-red-300">{error}</p>
      )}

      {link && (
        <div className="mt-3 p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
          <p className="text-sm font-medium text-green-800 dark:text-green-200 mb-2">
            Lien pour {link.username}, valable jusqu'à {formatTime(link.expiresAt)} (une seule utilisation)
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="text"
              readOnly
              value={link.resetUrl}
              onFocus={(event) => event.currentTarget.select()}
              aria-label="Lien de réinitialisation"
              className="flex-1 min-w-0 px-2 py-1 border border-green-300 dark:border-green-700 rounded bg-white dark:bg-gray-800 text-xs font-mono text-gray-900 dark:text-gray-100"
            />
            <CopyLinkButton url={link.resetUrl} />
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminResetLink;
